// src/components/tools/personal/JobSearchTool.jsx
// Job Search & CV Builder Tool - Integrates with ToolIndex and toolsService
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Search, FileText, Target, Mail, Clock, Briefcase, ChevronRight,
  Grid, Plus, Bookmark, Loader2, BookOpen, Edit2, Trash2, Download, Save,
  Sparkles, User, GraduationCap, Award, Users, ChevronDown, ChevronUp,
  MapPin, ExternalLink, Send, Building2, Filter, X, Upload, Paperclip,
  Calendar, Check, RefreshCw, DollarSign, XCircle, MinusCircle, SortAsc,
  Link, BookmarkCheck
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ============================================================
// CONSTANTS
// ============================================================

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  CV_HOME: 'CV_HOME',
  CV_FORM: 'CV_FORM',
  JOB_SEARCH: 'JOB_SEARCH',
  JOB_RESULTS: 'JOB_RESULTS',
  SAVED_JOBS: 'SAVED_JOBS',
  APPLICATION_FORM: 'APPLICATION_FORM',
  APPLICATION_TRACKER: 'APPLICATION_TRACKER',
};

const ApplicationStatus = {
  APPLIED: 'APPLIED',
  INTERVIEW_SCHEDULED: 'INTERVIEW_SCHEDULED',
  FOLLOW_UP_SENT: 'FOLLOW_UP_SENT',
  OFFER_RECEIVED: 'OFFER_RECEIVED',
  REJECTED: 'REJECTED',
  WITHDRAWN: 'WITHDRAWN'
};

const getStatusConfig = (status) => {
  const configs = {
    [ApplicationStatus.APPLIED]: { label: 'Applied', bg: 'bg-indigo-100', text: 'text-indigo-700', Icon: Mail },
    [ApplicationStatus.INTERVIEW_SCHEDULED]: { label: 'Interview', bg: 'bg-blue-100', text: 'text-blue-700', Icon: Calendar },
    [ApplicationStatus.FOLLOW_UP_SENT]: { label: 'Follow Up', bg: 'bg-cyan-100', text: 'text-cyan-700', Icon: RefreshCw },
    [ApplicationStatus.OFFER_RECEIVED]: { label: 'Offer', bg: 'bg-emerald-100', text: 'text-emerald-700', Icon: DollarSign },
    [ApplicationStatus.REJECTED]: { label: 'Rejected', bg: 'bg-red-100', text: 'text-red-700', Icon: XCircle },
    [ApplicationStatus.WITHDRAWN]: { label: 'Withdrawn', bg: 'bg-gray-100', text: 'text-gray-700', Icon: MinusCircle }
  };
  return configs[status] || configs[ApplicationStatus.APPLIED];
};

const fmtDate = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ============================================================
// JOB SEARCH SERVICE (uses generalWebSearch)
// ============================================================

// Correct mapping for generalWebSearch jobs response
const searchJobs = async (query, location, options = {}) => {
  try {
    const functions = getFunctions();
    const generalWebSearch = httpsCallable(functions, 'generalWebSearch');
    
    const result = await generalWebSearch({
      query: `${query} job vacancies hiring careers`,
      category: 'jobs',
      location: {
        city: location || '',
        country: options.country || 'ZA'
      },
      resultsCount: options.resultsCount || 15,
      bookId: options.bookId || null
    });

    if (!result.data?.success) {
      throw new Error(result.data?.error || 'Job search failed');
    }

    // Map cloud function response to component format
    return result.data.results.map((job) => ({
      id: job.id,                           // Already generated by cloud function
      title: job.title || 'Untitled Position',
      company: job.company || 'Unknown Company',
      location: job.location || location || 'Not specified',
      snippet: job.description || '',        // Cloud fn uses 'description'
      sourceUrl: job.weblink || '',          // Cloud fn uses 'weblink'
      sourceDomain: extractDomain(job.weblink || ''),
      email: job.email || '',
      phone: job.tel || '',                  // Cloud fn uses 'tel'
      salary: job.salary || '',
      linkedin: job.linkedin || '',
      postedDate: job.searchedAt?.split('T')[0] || new Date().toISOString().split('T')[0]
    }));
  } catch (error) {
    console.error('Job search error:', error);
    throw error;
  }
};

const extractDomain = (url) => {
  try {
    if (!url) return '';
    return new URL(url).hostname.replace('www.', '');
  } catch { return ''; }
};

const generateCoverEmail = async (jobTitle, company, cvData) => {
  // Try cloud function first, fallback to template
  try {
    const functions = getFunctions();
    const generateContent = httpsCallable(functions, 'generateContent');
    
    const result = await generateContent({
      prompt: `Write a professional cover email for ${jobTitle} at ${company}. Applicant: ${cvData?.personal?.fullName || 'Applicant'}. Summary: ${cvData?.summary || ''}`,
      type: 'text'
    });
    
    if (result.data?.content) return result.data.content;
  } catch (e) {
    console.log('Using template fallback');
  }
  
  // Fallback template
  const name = cvData?.personal?.fullName || '[Your Name]';
  return `Subject: Application for ${jobTitle} - ${name}

Dear Hiring Manager,

I am writing to express my interest in the ${jobTitle} position at ${company}.

${cvData?.summary || 'I bring relevant skills and experience that align with this role.'}

I have attached my CV for your review and would welcome the opportunity to discuss how I can contribute to your team.

Best regards,
${name}
${cvData?.personal?.email || ''}
${cvData?.personal?.phone || ''}`;
};

// ============================================================
// SHARED UI COMPONENTS
// ============================================================

const StatCard = ({ icon: Icon, label, value, color = 'indigo', onClick, subValue }) => (
  <button
    onClick={onClick}
    className={`flex items-start gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-50`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300 mt-2" />}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

const StatusBadge = ({ status }) => {
  const config = getStatusConfig(status);
  const Icon = config.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
};

const Card = ({ children, className = '', onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-2xl border border-gray-100 ${onClick ? 'cursor-pointer hover:shadow-lg hover:border-gray-200' : ''} transition-all ${className}`}
  >
    {children}
  </div>
);

// ============================================================
// CV BUILDER
// ============================================================

const CVBuilder = ({ cv, onSave, onCancel, saving, bookId }) => {
  const [formData, setFormData] = useState({
    personal: { fullName: '', email: '', phone: '', address: '', linkedin: '' },
    summary: '',
    education: [],
    experience: [],
    skills: [],
    references: [],
    ...cv
  });
  const [expandedSections, setExpandedSections] = useState({ personal: true, summary: true, education: true, experience: true, skills: true });
  const [newSkill, setNewSkill] = useState('');

  const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  const updatePersonal = (field, value) => setFormData(prev => ({ ...prev, personal: { ...prev.personal, [field]: value } }));

  const addEducation = () => setFormData(prev => ({ ...prev, education: [...prev.education, { id: Date.now(), institution: '', degree: '', field: '', startYear: '', endYear: '' }] }));
  const updateEducation = (index, field, value) => setFormData(prev => ({ ...prev, education: prev.education.map((e, i) => i === index ? { ...e, [field]: value } : e) }));
  const removeEducation = (index) => setFormData(prev => ({ ...prev, education: prev.education.filter((_, i) => i !== index) }));

  const addExperience = () => setFormData(prev => ({ ...prev, experience: [...prev.experience, { id: Date.now(), company: '', role: '', start: '', end: '', description: '' }] }));
  const updateExperience = (index, field, value) => setFormData(prev => ({ ...prev, experience: prev.experience.map((e, i) => i === index ? { ...e, [field]: value } : e) }));
  const removeExperience = (index) => setFormData(prev => ({ ...prev, experience: prev.experience.filter((_, i) => i !== index) }));

  const addSkill = () => { if (newSkill.trim()) { setFormData(prev => ({ ...prev, skills: [...prev.skills, newSkill.trim()] })); setNewSkill(''); } };
  const removeSkill = (index) => setFormData(prev => ({ ...prev, skills: prev.skills.filter((_, i) => i !== index) }));

  const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };

  const SectionHeader = ({ section, icon: Icon, title, count }) => (
    <button type="button" onClick={() => toggleSection(section)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-t-xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center"><Icon className="w-5 h-5 text-indigo-600" /></div>
        <div className="text-left">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {count !== undefined && <p className="text-sm text-gray-500">{count} items</p>}
        </div>
      </div>
      {expandedSections[section] ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
    </button>
  );

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><FileText className="w-6 h-6 text-indigo-600" />{cv?.id ? 'Edit CV' : 'Create CV'}</h1>
            <p className="text-gray-500">Build your professional curriculum vitae</p>
          </div>
        </div>
      </div>

      {/* Personal Details */}
      <Card className="overflow-hidden">
        <SectionHeader section="personal" icon={User} title="Personal Details" />
        {expandedSections.personal && (
          <div className="p-6 pt-0 grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Full Name *" value={formData.personal.fullName} onChange={(e) => updatePersonal('fullName', e.target.value)} className="px-4 py-2.5 border border-gray-200 rounded-xl" required />
            <input type="email" placeholder="Email *" value={formData.personal.email} onChange={(e) => updatePersonal('email', e.target.value)} className="px-4 py-2.5 border border-gray-200 rounded-xl" required />
            <input type="tel" placeholder="Phone" value={formData.personal.phone} onChange={(e) => updatePersonal('phone', e.target.value)} className="px-4 py-2.5 border border-gray-200 rounded-xl" />
            <input type="text" placeholder="Address" value={formData.personal.address} onChange={(e) => updatePersonal('address', e.target.value)} className="px-4 py-2.5 border border-gray-200 rounded-xl" />
            <input type="text" placeholder="LinkedIn" value={formData.personal.linkedin} onChange={(e) => updatePersonal('linkedin', e.target.value)} className="px-4 py-2.5 border border-gray-200 rounded-xl md:col-span-2" />
          </div>
        )}
      </Card>

      {/* Summary */}
      <Card className="overflow-hidden">
        <SectionHeader section="summary" icon={FileText} title="Professional Summary" />
        {expandedSections.summary && (
          <div className="p-6 pt-0">
            <textarea rows={5} placeholder="Write a compelling summary..." value={formData.summary} onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
          </div>
        )}
      </Card>

      {/* Education */}
      <Card className="overflow-hidden">
        <SectionHeader section="education" icon={GraduationCap} title="Education" count={formData.education.length} />
        {expandedSections.education && (
          <div className="p-6 pt-0 space-y-4">
            {formData.education.map((edu, index) => (
              <div key={edu.id || index} className="p-4 bg-gray-50 rounded-xl space-y-3 relative group">
                <button type="button" onClick={() => removeEducation(index)} className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input placeholder="Institution" value={edu.institution} onChange={(e) => updateEducation(index, 'institution', e.target.value)} className="px-4 py-2 border border-gray-200 rounded-xl" />
                  <input placeholder="Degree" value={edu.degree} onChange={(e) => updateEducation(index, 'degree', e.target.value)} className="px-4 py-2 border border-gray-200 rounded-xl" />
                  <input placeholder="Field of Study" value={edu.field} onChange={(e) => updateEducation(index, 'field', e.target.value)} className="px-4 py-2 border border-gray-200 rounded-xl" />
                  <div className="flex gap-2">
                    <input placeholder="Start Year" value={edu.startYear} onChange={(e) => updateEducation(index, 'startYear', e.target.value)} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl" />
                    <input placeholder="End Year" value={edu.endYear} onChange={(e) => updateEducation(index, 'endYear', e.target.value)} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl" />
                  </div>
                </div>
              </div>
            ))}
            <button type="button" onClick={addEducation} className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 flex items-center gap-2"><Plus className="w-4 h-4" />Add Education</button>
          </div>
        )}
      </Card>

      {/* Experience */}
      <Card className="overflow-hidden">
        <SectionHeader section="experience" icon={Briefcase} title="Work Experience" count={formData.experience.length} />
        {expandedSections.experience && (
          <div className="p-6 pt-0 space-y-4">
            {formData.experience.map((exp, index) => (
              <div key={exp.id || index} className="p-4 bg-gray-50 rounded-xl space-y-3 relative group">
                <button type="button" onClick={() => removeExperience(index)} className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input placeholder="Job Title" value={exp.role} onChange={(e) => updateExperience(index, 'role', e.target.value)} className="px-4 py-2 border border-gray-200 rounded-xl" />
                  <input placeholder="Company" value={exp.company} onChange={(e) => updateExperience(index, 'company', e.target.value)} className="px-4 py-2 border border-gray-200 rounded-xl" />
                  <input placeholder="Start Date" value={exp.start} onChange={(e) => updateExperience(index, 'start', e.target.value)} className="px-4 py-2 border border-gray-200 rounded-xl" />
                  <input placeholder="End Date" value={exp.end} onChange={(e) => updateExperience(index, 'end', e.target.value)} className="px-4 py-2 border border-gray-200 rounded-xl" />
                </div>
                <textarea placeholder="Description & achievements..." rows={3} value={exp.description} onChange={(e) => updateExperience(index, 'description', e.target.value)} className="w-full px-4 py-2 border border-gray-200 rounded-xl" />
              </div>
            ))}
            <button type="button" onClick={addExperience} className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 flex items-center gap-2"><Plus className="w-4 h-4" />Add Experience</button>
          </div>
        )}
      </Card>

      {/* Skills */}
      <Card className="overflow-hidden">
        <SectionHeader section="skills" icon={Award} title="Skills" count={formData.skills.length} />
        {expandedSections.skills && (
          <div className="p-6 pt-0 space-y-4">
            <div className="flex gap-2">
              <input type="text" value={newSkill} onChange={(e) => setNewSkill(e.target.value)} placeholder="Add a skill..." className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl" onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }} />
              <button type="button" onClick={addSkill} className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"><Plus className="w-5 h-5" /></button>
            </div>
            {formData.skills.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.skills.map((skill, index) => (
                  <span key={index} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-full text-sm">
                    {skill}
                    <button type="button" onClick={() => removeSkill(index)} className="hover:text-indigo-900"><X className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">Cancel</button>
        <button type="submit" disabled={saving} className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save CV'}
        </button>
      </div>
    </form>
  );
};

// ============================================================
// JOB SEARCH PANEL
// ============================================================

const JobSearchPanel = ({ onSearch, searching }) => {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('South Africa');

  const handleSubmit = (e) => { e.preventDefault(); if (query.trim()) onSearch(query, location); };

  return (
    <Card className="p-6 space-y-4">
      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Search className="w-5 h-5 text-indigo-600" />Find Your Next Opportunity</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title / Keywords</label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. Software Engineer" className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl" required />
            </div>
          </div>
          <div className="md:col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Cape Town" className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl" />
            </div>
          </div>
          <div className="md:col-span-3 flex items-end">
            <button type="submit" disabled={searching || !query.trim()} className="w-full py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium">
              {searching ? <><Loader2 className="w-5 h-5 animate-spin" />Searching...</> : <><Search className="w-5 h-5" />Search</>}
            </button>
          </div>
        </div>
      </form>
    </Card>
  );
};

// ============================================================
// JOB RESULTS LIST
// ============================================================

const JobResultsList = ({ results, onSaveJob, onApply, savedJobs = [], lastSearchQuery }) => {
  if (!results?.length) {
    return <EmptyState icon={Briefcase} title="No Jobs Found" description={`Try different keywords for "${lastSearchQuery?.query}"`} />;
  }

  const isJobSaved = (job) => savedJobs.some(s => s.sourceUrl === job.sourceUrl);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-900">{results.length} Jobs Found</h2>
      {results.map((job) => (
        <Card key={job.id} className="p-5 hover:shadow-md">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-indigo-600">{job.title}</h3>
              <div className="flex items-center gap-2 mt-1"><Building2 className="w-4 h-4 text-gray-400" /><span className="font-medium text-gray-900">{job.company}</span></div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 text-sm text-gray-500">
                {job.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{job.location}</span>}
                {job.salary && <span className="flex items-center gap-1 text-emerald-600 font-medium"><DollarSign className="w-4 h-4" />{job.salary}</span>}
                {job.sourceDomain && <span className="flex items-center gap-1"><Link className="w-3.5 h-3.5" />{job.sourceDomain}</span>}
              </div>
              {job.snippet && <p className="mt-3 text-sm text-gray-600 line-clamp-3">{job.snippet}</p>}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={() => onApply(job)} className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 text-sm font-medium"><Send className="w-4 h-4" />Apply</button>
              <button onClick={() => onSaveJob(job)} disabled={isJobSaved(job)} className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-medium ${isJobSaved(job) ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {isJobSaved(job) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}{isJobSaved(job) ? 'Saved' : 'Save'}
              </button>
              {job.sourceUrl && <a href={job.sourceUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm justify-center"><ExternalLink className="w-4 h-4" />View</a>}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

// ============================================================
// APPLICATION FORM
// ============================================================

const ApplicationForm = ({ job, cvs, onSaveApplication, onCancel, saving }) => {
  const [selectedCvId, setSelectedCvId] = useState(cvs[0]?.id || '');
  const [employerEmail, setEmployerEmail] = useState(job?.email || '');
  const [coverEmail, setCoverEmail] = useState('');
  const [generating, setGenerating] = useState(false);

  const selectedCv = cvs.find(c => c.id === selectedCvId);

  const handleGenerate = async () => {
    setGenerating(true);
    const email = await generateCoverEmail(job.title, job.company, selectedCv);
    setCoverEmail(email);
    setGenerating(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedCvId || !employerEmail || !coverEmail) { alert('Please fill all required fields'); return; }
    onSaveApplication({
      jobId: job.id,
      title: job.title,
      employer: job.company,
      location: job.location,
      sourceUrl: job.sourceUrl,
      employerEmail,
      status: ApplicationStatus.APPLIED,
      emailBody: coverEmail,
      cvId: selectedCvId,
      appliedAt: new Date().toISOString()
    });
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
        <div><h1 className="text-2xl font-bold text-gray-900">Apply: {job.title}</h1><p className="text-gray-500">{job.company}</p></div>
      </div>

      <Card className="p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" />Select CV</h3>
        <select value={selectedCvId} onChange={(e) => setSelectedCvId(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" required>
          {cvs.map(cv => <option key={cv.id} value={cv.id}>{cv.personal?.fullName || 'Unnamed CV'}</option>)}
        </select>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" />Employer Email</h3>
        <input type="email" value={employerEmail} onChange={(e) => setEmployerEmail(e.target.value)} placeholder="hr@company.com" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" required />
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" />Cover Email</h3>
          <button type="button" onClick={handleGenerate} disabled={generating} className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 text-sm">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}{generating ? 'Generating...' : 'AI Generate'}
          </button>
        </div>
        <textarea rows={12} value={coverEmail} onChange={(e) => setCoverEmail(e.target.value)} placeholder="Write your cover email..." className="w-full px-4 py-3 border border-gray-200 rounded-xl font-mono text-sm" required />
      </Card>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">Cancel</button>
        <button type="submit" disabled={saving} className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{saving ? 'Sending...' : 'Submit Application'}
        </button>
      </div>
    </form>
  );
};

// ============================================================
// APPLICATIONS TRACKER
// ============================================================

const ApplicationsTracker = ({ applications, onUpdateStatus, onDelete, onBack }) => {
  const handleStatusChange = async (app, newStatus) => {
    await onUpdateStatus(app.id, { ...app, status: newStatus, updatedAt: new Date().toISOString() });
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
        <div><h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Target className="w-6 h-6 text-emerald-600" />Application Tracker</h1><p className="text-gray-500">{applications.length} applications</p></div>
      </div>

      {applications.length === 0 ? (
        <EmptyState icon={Target} title="No Applications" description="Start applying for jobs to track your progress" />
      ) : (
        <div className="space-y-4">
          {applications.map(app => (
            <Card key={app.id} className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">{app.title}</h3>
                  <p className="text-indigo-600 font-medium">{app.employer}</p>
                  <p className="text-sm text-gray-500 mt-1">Applied {fmtDate(app.appliedAt)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <select value={app.status} onChange={(e) => handleStatusChange(app, e.target.value)} className="px-3 py-2 border border-gray-200 rounded-xl text-sm">
                    {Object.entries(ApplicationStatus).map(([key, val]) => <option key={key} value={val}>{getStatusConfig(val).label}</option>)}
                  </select>
                  <StatusBadge status={app.status} />
                  <button onClick={() => onDelete(app.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const JobSearchTool = ({ bookId, customStyling = {}, toolsService, onBack, installationId, workspaceId = 'default' }) => {
  const [viewState, setViewState] = useState(ViewState.DASHBOARD);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);

  const [cvs, setCvs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [applications, setApplications] = useState([]);

  const [editingCv, setEditingCv] = useState(null);
  const [jobResults, setJobResults] = useState([]);
  const [lastSearchQuery, setLastSearchQuery] = useState({ query: '', location: '' });
  const [jobToApply, setJobToApply] = useState(null);

  useEffect(() => { loadData(); }, [bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cvsData, savedJobsData, applicationsData] = await Promise.all([
        toolsService.getToolData(bookId, 'job-cvs', workspaceId),
        toolsService.getToolData(bookId, 'saved-jobs', workspaceId),
        toolsService.getToolData(bookId, 'job-applications', workspaceId)
      ]);
      setCvs(cvsData || []);
      setSavedJobs(savedJobsData || []);
      setApplications(applicationsData || []);
    } catch (error) { console.error('Error loading data:', error); }
    finally { setLoading(false); }
  };

  // Handlers
  const handleSaveCV = async (data) => {
    setSaving(true);
    try { await toolsService.saveToolData(bookId, 'job-cvs', workspaceId, data); await loadData(); setEditingCv(null); setViewState(ViewState.CV_HOME); }
    catch (error) { console.error('Error saving CV:', error); alert('Failed to save CV'); }
    finally { setSaving(false); }
  };

  const handleSearchJobs = async (query, location) => {
    setSearching(true);
    try {
      const results = await searchJobs(query, location, { bookId });
      setJobResults(results);
      setLastSearchQuery({ query, location });
      setViewState(ViewState.JOB_RESULTS);
    } catch (error) { console.error('Job search failed:', error); alert('Job search failed'); }
    finally { setSearching(false); }
  };

  const handleSaveJob = async (job) => {
    if (savedJobs.find(s => s.sourceUrl === job.sourceUrl)) return;
    await toolsService.saveToolData(bookId, 'saved-jobs', workspaceId, { ...job, savedAt: new Date().toISOString() });
    await loadData();
  };

  const handleOpenApplication = (job) => {
    if (cvs.length === 0) { alert('Create a CV first'); setViewState(ViewState.CV_FORM); return; }
    setJobToApply(job);
    setViewState(ViewState.APPLICATION_FORM);
  };

  const handleSaveApplication = async (data) => {
    setSaving(true);
    try { await toolsService.saveToolData(bookId, 'job-applications', workspaceId, data); await loadData(); setJobToApply(null); setViewState(ViewState.APPLICATION_TRACKER); }
    catch (error) { console.error('Error:', error); alert('Failed to save application'); }
    finally { setSaving(false); }
  };

  const handleUpdateApplication = async (id, data) => {
    await toolsService.saveToolData(bookId, 'job-applications', workspaceId, data);
    await loadData();
  };

  const handleDeleteApplication = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    await toolsService.deleteToolData(bookId, 'job-applications', workspaceId, id);
    await loadData();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-10 h-10 text-indigo-600 animate-spin" /></div>;

  // CV Builder
  if (viewState === ViewState.CV_FORM) return <div className="h-full overflow-y-auto bg-gray-50"><CVBuilder cv={editingCv} onSave={handleSaveCV} onCancel={() => setViewState(ViewState.CV_HOME)} saving={saving} bookId={bookId} /></div>;

  // CV Home
  if (viewState === ViewState.CV_HOME) {
    return (
      <div className="h-full overflow-y-auto bg-gray-50">
        <div className="max-w-6xl mx-auto p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setViewState(ViewState.DASHBOARD)} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
              <div><h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><FileText className="w-6 h-6 text-purple-600" />CV Builder</h1></div>
            </div>
            <button onClick={() => { setEditingCv(null); setViewState(ViewState.CV_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"><Plus className="w-4 h-4" />New CV</button>
          </div>
          {cvs.length === 0 ? <EmptyState icon={FileText} title="No CV" description="Create your CV to start applying" action={<button onClick={() => setViewState(ViewState.CV_FORM)} className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"><Plus className="w-5 h-5" />Create CV</button>} /> : (
            <div className="space-y-4">
              {cvs.map(cv => (
                <Card key={cv.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div><h2 className="text-xl font-bold text-gray-900">{cv.personal?.fullName || 'Unnamed'}</h2><p className="text-gray-600">{cv.personal?.email}</p><p className="text-sm text-gray-500 mt-2">Updated: {fmtDate(cv.updatedAt || cv.createdAt)}</p></div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingCv(cv); setViewState(ViewState.CV_FORM); }} className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700">Edit</button>
                      <button onClick={() => toolsService.deleteToolData(bookId, 'job-cvs', workspaceId, cv.id).then(loadData)} className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl">Delete</button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Job Search & Results
  if (viewState === ViewState.JOB_SEARCH || viewState === ViewState.JOB_RESULTS) {
    return (
      <div className="h-full overflow-y-auto bg-gray-50">
        <div className="max-w-6xl mx-auto p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setViewState(ViewState.DASHBOARD)} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
              <div><h1 className="text-2xl font-bold text-gray-900">Job Search</h1><p className="text-gray-500">AI-powered job discovery</p></div>
            </div>
            <button onClick={() => setViewState(ViewState.DASHBOARD)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl flex items-center gap-2"><Grid className="w-4 h-4" />Dashboard</button>
          </div>
          <JobSearchPanel onSearch={handleSearchJobs} searching={searching} />
          {viewState === ViewState.JOB_RESULTS && <JobResultsList results={jobResults} onSaveJob={handleSaveJob} onApply={handleOpenApplication} savedJobs={savedJobs} lastSearchQuery={lastSearchQuery} />}
        </div>
      </div>
    );
  }

  // Application Form
  if (viewState === ViewState.APPLICATION_FORM && jobToApply) {
    return <div className="h-full overflow-y-auto bg-gray-50"><ApplicationForm job={jobToApply} cvs={cvs} onSaveApplication={handleSaveApplication} onCancel={() => setViewState(ViewState.JOB_RESULTS)} saving={saving} /></div>;
  }

  // Application Tracker
  if (viewState === ViewState.APPLICATION_TRACKER) {
    return <div className="h-full overflow-y-auto bg-gray-50"><ApplicationsTracker applications={applications} onUpdateStatus={handleUpdateApplication} onDelete={handleDeleteApplication} onBack={() => setViewState(ViewState.DASHBOARD)} /></div>;
  }

  // Dashboard
  const inProgress = applications.filter(a => [ApplicationStatus.APPLIED, ApplicationStatus.INTERVIEW_SCHEDULED, ApplicationStatus.FOLLOW_UP_SENT].includes(a.status)).length;

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl"><ArrowLeft className="w-5 h-5 text-gray-600" /></button>
            <div><h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Briefcase className="w-7 h-7 text-indigo-600" />Job Search & Career Tool</h1><p className="text-gray-500">Find, apply, and track your career</p></div>
          </div>
          <button onClick={() => setViewState(ViewState.JOB_SEARCH)} className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"><Search className="w-4 h-4" />Search Jobs</button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={FileText} label="Total CVs" value={cvs.length} color="purple" onClick={() => setViewState(ViewState.CV_HOME)} />
          <StatCard icon={Bookmark} label="Saved Jobs" value={savedJobs.length} color="amber" onClick={() => setViewState(ViewState.SAVED_JOBS)} />
          <StatCard icon={Mail} label="Applications" value={applications.length} color="indigo" onClick={() => setViewState(ViewState.APPLICATION_TRACKER)} />
          <StatCard icon={Clock} label="In Progress" value={inProgress} color="emerald" onClick={() => setViewState(ViewState.APPLICATION_TRACKER)} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card onClick={() => setViewState(ViewState.JOB_SEARCH)} className="p-6 cursor-pointer hover:shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mb-3"><Search className="w-7 h-7 text-indigo-600" /></div>
              <h3 className="font-semibold text-gray-900">Job Search</h3><p className="text-sm text-gray-500 mt-1">AI-powered discovery</p>
            </div>
          </Card>
          <Card onClick={() => setViewState(ViewState.CV_HOME)} className="p-6 cursor-pointer hover:shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-3"><FileText className="w-7 h-7 text-purple-600" /></div>
              <h3 className="font-semibold text-gray-900">CV Builder</h3><p className="text-sm text-gray-500 mt-1">Create & export</p>
            </div>
          </Card>
          <Card onClick={() => setViewState(ViewState.APPLICATION_TRACKER)} className="p-6 cursor-pointer hover:shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-3"><Target className="w-7 h-7 text-emerald-600" /></div>
              <h3 className="font-semibold text-gray-900">Tracker</h3><p className="text-sm text-gray-500 mt-1">Monitor pipeline</p>
            </div>
          </Card>
        </div>

        {applications.length > 0 && (
          <Card className="overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Recent Applications</h2>
              <button onClick={() => setViewState(ViewState.APPLICATION_TRACKER)} className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">View All<ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="divide-y divide-gray-50">
              {applications.slice(0, 5).map(app => (
                <div key={app.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex-1 min-w-0"><p className="font-medium text-gray-900 truncate">{app.title}</p><p className="text-sm text-gray-500">{app.employer} • {fmtDate(app.appliedAt)}</p></div>
                  <StatusBadge status={app.status} />
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default JobSearchTool;